import { CalendarDays, ChevronDownIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SELECT_TRIGGER_CLASS } from "@/components/ui/select";
import { calendarWeeks, isDay, shiftDay, shiftMonth } from "@/lib/calendar";
import { belgianDay, formatDay, formatLongDay, formatMonth, weekdayOf } from "@/lib/format";
import { cn } from "@/lib/utils";

/** A Monday to Sunday, for the calendar's weekday names. */
const WEEK = [
  "2024-01-01",
  "2024-01-02",
  "2024-01-03",
  "2024-01-04",
  "2024-01-05",
  "2024-01-06",
  "2024-01-07",
];

/** What an arrow key moves the day in focus by. */
const STEPS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

interface DayPickerProps {
  /** A calendar day ("2026-11-15"), or null for none. */
  value: string | null;
  onChange: (day: string | null) => void;
  /** The control's name for screen readers: "Deadline". */
  label: string;
  /** What the button says while no day is chosen: "Geen deadline". */
  placeholder: string;
  id?: string;
  className?: string;
  /** Takes the focus when it shows, as the first control of a form opened in place. */
  autoFocus?: boolean;
  /** Offers "Wissen", which chooses no day; leave it off where a day is required. */
  clearable?: boolean;
}

/**
 * A choice of calendar day in the dashboard's theme (never the browser's own
 * date picker): a button like `<SelectTrigger>` opens a month's calendar,
 * Monday first, with today outlined and the chosen day in De Ceuster blue.
 * ‹ and › step months; "Wissen" chooses no day. Keyboard: the arrows move a
 * day or a week (into the next month too), Enter picks, Escape closes. Days
 * are calendar days as the API writes them ("2026-11-15"), in Belgian time.
 *
 *     <DayPicker value={deadline} onChange={setDeadline} label="Deadline" placeholder="Geen deadline" />
 */
export function DayPicker({
  value,
  onChange,
  label,
  placeholder,
  id,
  className,
  autoFocus,
  clearable = true,
}: DayPickerProps) {
  const { t } = useTranslation();
  const today = belgianDay(new Date()) ?? "";
  const [open, setOpen] = useState(false);
  // The day the keyboard is on, which also decides the month on show.
  const [focusDay, setFocusDay] = useState(() => (value && isDay(value) ? value : today));
  const grid = useRef<HTMLDivElement>(null);
  // Only after an arrow key: the day in focus follows the keyboard.
  const moved = useRef(false);
  const month = focusDay.slice(0, 7);

  useEffect(() => {
    if (moved.current) {
      moved.current = false;
      grid.current?.querySelector<HTMLButtonElement>(`[data-day="${focusDay}"]`)?.focus();
    }
  }, [focusDay]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next) {
      setFocusDay(value && isDay(value) ? value : today);
    }
  };

  const pick = (day: string | null) => {
    onChange(day);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = STEPS[event.key];
    if (step !== undefined) {
      event.preventDefault();
      moved.current = true;
      setFocusDay((day) => shiftDay(day, step));
    }
  };

  const stepMonth = (by: number) => setFocusDay(`${shiftMonth(month, by)}-01`);

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          autoFocus={autoFocus}
          data-size="sm"
          aria-label={value ? t("dayPicker.value", { label, day: formatLongDay(value) }) : label}
          className={cn(SELECT_TRIGGER_CLASS, "w-48", className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarDays aria-hidden="true" className="text-muted size-4" />
            <span className={cn("truncate", !value && "text-muted")}>
              {value ? formatDay(value) : placeholder}
            </span>
          </span>
          <ChevronDownIcon aria-hidden="true" className="text-muted size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-3"
        onOpenAutoFocus={(event) => {
          // Into the calendar, on the chosen day or today, rather than on ‹.
          const day = grid.current?.querySelector<HTMLButtonElement>('[tabindex="0"]');
          if (day) {
            event.preventDefault();
            day.focus();
          }
        }}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t("dayPicker.previousMonth")}
            onClick={() => stepMonth(-1)}
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
          </Button>
          <p
            aria-live="polite"
            className="text-foreground text-sm font-semibold first-letter:uppercase"
          >
            {formatMonth(month)}
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t("dayPicker.nextMonth")}
            onClick={() => stepMonth(1)}
          >
            <ChevronRight aria-hidden="true" className="size-4" />
          </Button>
        </div>
        <div ref={grid} role="group" aria-label={formatMonth(month)} onKeyDown={onKeyDown}>
          <div aria-hidden="true" className="text-muted grid grid-cols-7 text-center text-xs">
            {WEEK.map((day) => (
              <span key={day} className="py-1">
                {weekdayOf(day)}
              </span>
            ))}
          </div>
          {calendarWeeks(month).map((week) => (
            <div key={week.find(Boolean)} className="grid grid-cols-7">
              {week.map((day, index) =>
                day ? (
                  <button
                    key={day}
                    type="button"
                    data-day={day}
                    tabIndex={day === focusDay ? 0 : -1}
                    aria-label={formatLongDay(day)}
                    aria-pressed={day === value}
                    aria-current={day === today ? "date" : undefined}
                    onClick={() => pick(day)}
                    className={cn(
                      "focus-visible:ring-ring m-0.5 size-8 rounded-sm text-sm tabular-nums focus-visible:ring-2 focus-visible:outline-none pointer-coarse:size-10",
                      day === value
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground hover:bg-accent-light hover:text-primary",
                      day === today && day !== value && "border-primary border",
                    )}
                  >
                    {Number(day.slice(8))}
                  </button>
                ) : (
                  <span key={`empty-${index}`} aria-hidden="true" />
                ),
              )}
            </div>
          ))}
        </div>
        {clearable && value && (
          <div className="mt-2 flex justify-end border-t pt-2">
            <Button variant="ghost" size="sm" onClick={() => pick(null)}>
              {t("dayPicker.clear")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
